<template>
  <div class="i-personal-information">
    <div class="inputs">
      <div class="row">
        <div class="label">Full Name</div>
        <div class="field">
          <input
            v-model="data.fullName"
            class="input"
            type="text"
            placeholder="Full Name"
            :disabled="!data.allowEdit"
          />
          <div
            v-if="data.showFullnameError"
            class="error"
          >
            Your name is required.
          </div>
        </div>
      </div>
      <div class="row">
        <div class="label">Gender</div>
        <select
          v-model="data.gender"
          class="input"
          name="gender"
          placeholder="Gender"
          :disabled="!data.allowEdit"
        >
          <button>
            <selectedcontent></selectedcontent>
          </button>
          <option
            v-for="gender in GenderValues"
            :key="gender"
            :value="gender"
          >
            {{ gender.charAt(0).toUpperCase() + gender.slice(1) }}
          </option>
        </select>
      </div>
      <div class="row">
        <div class="label">Birthday</div>
        <div class="field">
          <input
            v-model="data.birthday"
            class="input"
            type="date"
            placeholder="Birthday"
            :min="birthdayMinString"
            :max="birthdayMaxString"
            :disabled="!data.allowEdit"
          />
          <div
            v-if="data.showBirthdayError"
            class="error"
          >
            Birthday must be between {{ birthdayMinString }} and {{ birthdayMaxString }}.
          </div>
        </div>
      </div>
    </div>
    <div class="buttons">
      <iBaseButton
        v-if="!data.allowEdit"
        v-bind="OTHER_BUTTON_STYLE"
        text="Edit Details"
        @clicked="data.allowEdit = true"
      />
      <iBaseButton
        v-if="data.allowEdit && !data.isSaving"
        v-bind="OTHER_BUTTON_STYLE"
        text="Cancel"
        @clicked="cancelEdit()"
      />
      <iBaseButton
        v-if="data.allowEdit"
        v-bind="MAIN_BUTTON_STYLE"
        :text="data.isSaving ? 'Saving...' : 'Save Changes'"
        :is-disabled="data.isSaving"
        @clicked="saveChanges()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue"
import { useUserStore } from "@/stores/user"
import { useReportStore } from "@/stores/report"
import { azureRadiance600, black600, white } from "@/styles/colors"

import { GlobalFunctions } from "@/helpers/globalFunctions"
import api from "@/services/apiClient"

import iBaseButton from "@/components/base/BaseButton.vue"

// TYPES
import { GenderType, GenderValues } from "@/shared/dtos/in/UserPostIn"
import UserPutMeIn from "@/shared/dtos/in/UserPutMeIn"

// STORES
const userStore = useUserStore()
const reportStore = useReportStore()

// COSTANTS
const MAIN_BUTTON_STYLE = {
  backgroundColor: azureRadiance600,
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "fit-content",
  padding: "10px 16px",
  size: "small"
}
const OTHER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: black600,
  width: "fit-content",
  padding: "10px 16px",
  size: "small",
  version: "outline"
}
const thisYear = new Date().getFullYear()
const birthdayMinString = `${thisYear - 90}-01-01`
const birthdayMaxString = `${thisYear - 11}-12-31`
const birthdayMinDate = GlobalFunctions.convertStringToLocaleDate(birthdayMinString)
const birthdayMaxDate = GlobalFunctions.convertStringToLocaleDate(birthdayMaxString)

//DATA
const data = reactive({
  isSaving: false,
  allowEdit: false,
  showFullnameError: false,
  showBirthdayError: false,
  fullName: userStore.getFullName || "",
  gender: (userStore.getGender || "other") as GenderType,
  birthday: (userStore.getBirthday || "").toString().split("T")[0]
})

// FUNCTIONS
const cancelEdit = () => {
  hideErrors()
  data.fullName = userStore.getFullName || ""
  data.gender = (userStore.getGender || "other") as GenderType
  data.birthday = (userStore.getBirthday || "").toString().split("T")[0]
  data.allowEdit = false
}

const hideErrors = () => {
  data.showBirthdayError = false
  data.showFullnameError = false
}

const isDataValidated = () => {
  hideErrors()

  // Check that fullName is provided
  if (!data.fullName.trim()) data.showFullnameError = true

  // Check that if birthday is provided, it is within the limit
  const birthdayToSave = GlobalFunctions.convertStringToLocaleDate(data.birthday)
  if (!!data.birthday && (birthdayToSave > birthdayMaxDate || birthdayToSave < birthdayMinDate))
    data.showBirthdayError = true

  return !data.showBirthdayError && !data.showFullnameError
}

const saveChanges = async () => {
  if (isDataValidated()) {
    data.isSaving = true
    try {
      const putData = {
        fullName: data.fullName,
        gender: data.gender,
        birthday: GlobalFunctions.convertStringToLocaleDate(data.birthday)
      } as UserPutMeIn
      await api.user.putMe(putData)
      await userStore.loadUserData()
      await reportStore.loadReport()

      data.allowEdit = false
    } catch (err) {
      console.error("Failed to update user data:", err)
      cancelEdit()
    }
    data.isSaving = false
  }
}
</script>

<style scoped lang="scss">
.i-personal-information {
  @include flex(column, start, stretch, $gap: 30px);

  .inputs {
    @include flex(column, start, stretch, $gap: 15px);

    @media screen and (min-width: $breakpoint-sm) {
      gap: 25px;
    }

    .row {
      @include flex(column, start, start);

      border-bottom: 1px solid $zumthor100;
      padding-bottom: 15px;

      @media screen and (min-width: $breakpoint-sm) {
        @include flex(row, start, center);

        padding-bottom: 25px;
      }

      .label {
        @include text-sm;

        margin-bottom: 10px;
        font-weight: 600;

        @media screen and (min-width: $breakpoint-sm) {
          margin-bottom: 0;
          width: 170px;
          flex: 0 0 170px;
        }

        @media screen and (min-width: $breakpoint-md) {
          width: 250px;
          flex: 0 0 250px;
        }
      }

      .field {
        @include flex(column, start, start);

        width: 100%;

        .error {
          @include text-xs;

          margin-top: 5px;
          color: $bittersweet600;
        }
      }

      .input {
        @include border-radius-sm;
        @include text-sm;

        border: 1px solid $black200;
        color: $comet600;
        min-height: 40px;
        outline: none;
        padding: 5px 10px;
        width: 100%;

        &:disabled {
          background-color: $black50;
          opacity: 1;
        }
      }
    }
  }

  .buttons {
    @include flex(row, start, center, $gap: 20px);
  }

  // styling for select and option below
  /* stylelint-disable */
  ::picker(select) {
    appearance: base-select;
    border: none;
  }
  /* stylelint-enable */

  select {
    @include flex(row, space-between, center);

    appearance: base-select;
    padding: 5px 15px 5px 10px !important;
    cursor: pointer;

    &:hover,
    &:focus {
      background: $white;
    }
  }

  option {
    display: flex;
    justify-content: flex-start;
    gap: 10px;
    border: 1px solid $zumthor100;
    color: $comet600;
    min-height: 40px;
    padding: 5px 10px;

    &:focus {
      background: $azure-radiance100;
    }

    &:hover {
      background: $azure-radiance50;
    }

    &:first-of-type {
      border-radius: 8px 8px 0 0;
    }

    &:last-of-type {
      border-radius: 0 0 8px 8px;
    }

    &:not(option:last-of-type) {
      border-bottom: none;
    }
  }
}
</style>
